/**
 * EAN-13 variable measure (balanza): prefijo 2x, PLU 5 dígitos, valor embebido 5 dígitos.
 *
 * Perfil por sucursal: el POS no adivina si los 5 dígitos son gramos, centavos o pesos.
 * Default `gs1_prefix` = GS1 de circulación restringida: 20–24 gramos · 25–28 centavos.
 */

export type WeightBarcodeMode = 'weight' | 'price';

/** Cómo interpreta la sucursal el EAN-13 de la etiquetadora. */
export type ScaleBarcodeProfile =
  | 'gs1_prefix'
  | 'ean13_255_price_pesos'
  | 'ean13_255_weight_grams';

export const SCALE_BARCODE_PROFILES: readonly ScaleBarcodeProfile[] = [
  'gs1_prefix',
  'ean13_255_price_pesos',
  'ean13_255_weight_grams',
] as const;

export type WeightBarcodeEmbeddedUnit = 'grams' | 'cents' | 'pesos';

export type ParsedWeightBarcode = {
  raw: string;
  prefix: string;
  plu: string;
  mode: WeightBarcodeMode;
  /** Gramos, centavos ARS o pesos enteros según `embeddedUnit`. */
  embeddedValue: number;
  embeddedUnit: WeightBarcodeEmbeddedUnit;
};

const WEIGHT_PREFIXES = new Set(['20', '21', '22', '23', '24']);
const PRICE_PREFIXES = new Set(['25', '26', '27', '28']);
const RESTRICTED_2X_PREFIXES = new Set([
  '20',
  '21',
  '22',
  '23',
  '24',
  '25',
  '26',
  '27',
  '28',
  '29',
]);

const MIN_CART_QTY = 0.001;
const MAX_CART_QTY = 99.999;

/** Normaliza PLU balanza a 5 dígitos (cero a la izquierda). */
export function normalizeScalePlu(plu: string): string {
  const digits = plu.replace(/\D/g, '');
  if (!digits) return '';
  return digits.slice(-5).padStart(5, '0');
}

/** Quita ceros a la izquierda del PLU para matchear SKU cortos en catálogo. */
export function trimScalePluLeadingZeros(plu: string): string {
  const normalized = normalizeScalePlu(plu);
  const trimmed = normalized.replace(/^0+/, '');
  return trimmed || '0';
}

export function parseScaleBarcodeProfile(raw: unknown): ScaleBarcodeProfile {
  if (raw === 'ean13_255_price_pesos' || raw === 'ean13_255_weight_grams') return raw;
  return 'gs1_prefix';
}

export function computeEan13CheckDigit(data12: string): number {
  if (!/^\d{12}$/.test(data12)) {
    throw new Error('EAN-13 check digit requires 12 digits');
  }
  let sum = 0;
  for (let i = 0; i < 12; i += 1) {
    const digit = Number(data12[i]);
    sum += digit * (i % 2 === 0 ? 1 : 3);
  }
  return (10 - (sum % 10)) % 10;
}

function buildVariableMeasureBarcodeEan13(
  plu: string,
  embeddedValue: number,
  prefix: string,
): string {
  const plu5 = normalizeScalePlu(plu);
  const value5 = String(Math.max(0, Math.min(99999, Math.round(embeddedValue)))).padStart(5, '0');
  const base12 = `${prefix}${plu5}${value5}`;
  if (!/^\d{12}$/.test(base12)) {
    throw new Error('Invalid variable-measure barcode base');
  }
  return `${base12}${computeEan13CheckDigit(base12)}`;
}

/** Arma EAN-13 pesable válido (modo peso, prefijo 20). Útil en tests y seed E2E. */
export function buildWeightBarcodeEan13(plu: string, grams: number, prefix = '20'): string {
  return buildVariableMeasureBarcodeEan13(plu, grams, prefix);
}

/** Arma EAN-13 con precio embebido en centavos (prefijo 25). */
export function buildPriceBarcodeEan13(plu: string, cents: number, prefix = '25'): string {
  return buildVariableMeasureBarcodeEan13(plu, cents, prefix);
}

/** Arma EAN-13 2-5-5 con importe en pesos enteros (prefijo 20, layout carnicería). */
export function buildPricePesosBarcodeEan13(plu: string, pesos: number, prefix = '20'): string {
  return buildVariableMeasureBarcodeEan13(plu, pesos, prefix);
}

function validateEan13CheckDigit(digits: string): boolean {
  if (digits.length !== 13) return false;
  const expected = computeEan13CheckDigit(digits.slice(0, 12));
  return expected === Number(digits[12]);
}

function resolveGs1PrefixMode(prefix: string): WeightBarcodeMode | null {
  if (WEIGHT_PREFIXES.has(prefix)) return 'weight';
  if (PRICE_PREFIXES.has(prefix)) return 'price';
  return null;
}

/**
 * Parsea barcode pesable de balanza. Devuelve null si no aplica o dígito verificador inválido.
 * Sin perfil (o `gs1_prefix`) el comportamiento es el histórico: 20–24 gramos, 25–28 centavos.
 */
export function parseWeightBarcode(
  raw: string,
  profile: ScaleBarcodeProfile = 'gs1_prefix',
): ParsedWeightBarcode | null {
  const digits = raw.trim().replace(/\D/g, '');
  if (digits.length !== 13 || digits[0] !== '2') {
    return null;
  }
  if (!validateEan13CheckDigit(digits)) {
    return null;
  }

  const prefix = digits.slice(0, 2);
  const plu = digits.slice(2, 7);
  const embeddedValue = Number(digits.slice(7, 12));
  if (!Number.isFinite(embeddedValue)) {
    return null;
  }

  if (profile === 'ean13_255_price_pesos') {
    if (!RESTRICTED_2X_PREFIXES.has(prefix)) return null;
    return {
      raw: digits,
      prefix,
      plu,
      mode: 'price',
      embeddedValue,
      embeddedUnit: 'pesos',
    };
  }

  if (profile === 'ean13_255_weight_grams') {
    if (!RESTRICTED_2X_PREFIXES.has(prefix)) return null;
    return {
      raw: digits,
      prefix,
      plu,
      mode: 'weight',
      embeddedValue,
      embeddedUnit: 'grams',
    };
  }

  const mode = resolveGs1PrefixMode(prefix);
  if (!mode) return null;

  return {
    raw: digits,
    prefix,
    plu,
    mode,
    embeddedValue,
    embeddedUnit: mode === 'weight' ? 'grams' : 'cents',
  };
}

function clampCartQuantity(qty: number): number | null {
  if (!Number.isFinite(qty) || qty < MIN_CART_QTY || qty > MAX_CART_QTY) {
    return null;
  }
  return qty;
}

/**
 * Convierte parseo balanza → cantidad en la UoM del producto (kg o g).
 * Modo precio: total embebido / precio unitario.
 * Pesos: kg a 3 decimales; qty fuera de 0.001–99.999 → null (no meter 67 kg por error).
 */
export function weightBarcodeToCartQuantity(
  parsed: ParsedWeightBarcode,
  unitPrice: number,
  unitOfMeasure: 'kg' | 'g',
): number | null {
  if (!Number.isFinite(unitPrice) || unitPrice <= 0) {
    return null;
  }

  if (parsed.mode === 'weight' || parsed.embeddedUnit === 'grams') {
    const grams = parsed.embeddedValue;
    if (grams <= 0) return null;
    if (unitOfMeasure === 'kg') {
      return grams / 1000;
    }
    return grams;
  }

  const lineTotalArs =
    parsed.embeddedUnit === 'pesos' ? parsed.embeddedValue : parsed.embeddedValue / 100;
  if (lineTotalArs <= 0) return null;

  const rawQty = lineTotalArs / unitPrice;
  if (unitOfMeasure === 'kg') {
    const rounded = Math.round(rawQty * 1000) / 1000;
    return parsed.embeddedUnit === 'pesos' ? clampCartQuantity(rounded) : rounded;
  }
  const gramsQty = rawQty * 1000;
  return parsed.embeddedUnit === 'pesos' ? clampCartQuantity(gramsQty) : gramsQty;
}
